<template>
  <div style="height: 100%; display: flex; flex-direction: column;">
    <portal to="dashboard-title">{{ $t('dashboards.emsDashboard') }}</portal>
    <portal to="dashboard-footer">
      <span class="headline font-weight-medium">{{ now }} | {{ currentShift }}</span>
    </portal>

    <v-overlay
      :value="loading"
      z-index="9999"
      opacity="0.9"
      :color="$vuetify.theme.dark ? '#000000' : '#FFFFFF'"
    >
      <DashboardLoading />
    </v-overlay>

    <v-container
      fluid
      class="pa-0"
      v-resize="onResize"
      v-show="!loading"
      style="flex-grow: 1; height: 100%; overflow: hidden;"
    >
      <div class="d-flex align-center justify-center" style="width: 100%; height: 100%;">
        <div
          style="position: relative;"
          :style="`height: ${height}px; width: ${width}px;`"
        >
          <div
            v-if="!hasLayout"
            class="d-flex align-center justify-center h-full w-full"
            :class="$vuetify.theme.dark ? 'white--text' : 'black--text'"
          >
            <h2>{{ $t('dashboards.loadingLayout') }}</h2>
          </div>

          <div
            v-else
            class="w-full h-full"
            :style="{ fontSize: `${16 * scale}px` }"
          >
            <v-window v-model="window" style="flex: 1 1 auto; height: 100%;">
              <v-window-item
                v-for="(details, layoutKey, i) in layoutData"
                :key="`slide-${i}`"
                class="h-full"
              >
                <div class="w-full h-full" style="position: relative;">

                  <div
                    v-for="(meter, mIdx) in details.meters"
                    :key="`meter-${mIdx}`"
                    :style="meter.style"
                    style="position: absolute;"
                  >
                    <div class="h-full">
                      <div
                        class="text-center font-weight-bold"
                        :style="{
                          color: '#F5A623',
                          fontSize: `${fontsizeData.meter * scale}px`
                        }"
                      >
                        {{ meter.metername }}
                      </div>
                      <div class="d-flex justify-center align-center h-full mt-1 mb-1">
                        <img :src="getIconPath(meter.icon)"
                        width="30%"
                        height="auto"
                       />
                      </div>
                      <div
                        class="text-center mb-1"
                        :style="{
                          color: $vuetify.theme.dark ? '#fff' : '#000',
                          fontSize: `${fontsizeData.meter * scale}px`
                        }"
                      >
                        {{ meter.description }}
                      </div>
                    </div>

                    <div
                      class="h-full d-flex flex-column justify-center"
                      style="width: 100%; line-height: 1.7;"
                      :style="{ fontSize: `${fontsizeData.unit * scale}px` }"
                    >
                      <div
                        class="d-flex justify-space-between align-center"
                        v-for="unit in unitData"
                        :key="unit.value"
                        style="position: relative; margin: 1.5%; border: 1px solid;"
                        :style="{
                          borderColor: $vuetify.theme.dark ? '#424242' : '#B9B9B9',
                          background: $vuetify.theme.dark ? '#333' : '#F5F5F5'
                        }"
                      >
                        <div
                          :style="unit.style"
                          style="position: absolute;"
                          :class="$vuetify.theme.dark ? 'white--text' : 'black--text'"
                        >
                          {{ unit.label }}
                        </div>
                        <div
                          style="text-align: center; width: 100%; font-weight: bold;"
                          :class="$vuetify.theme.dark ? 'white--text' : 'black--text'"
                        >
                          {{ getUnitValue(meter.metername, unit.value) }}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    v-for="(line, lIdx) in details.lines"
                    :key="`line-${lIdx}`"
                    :style="line.style"
                    style="position: absolute;"
                    :id="line.name"
                  ></div>

                  <div
                    v-for="(section, sIdx) in details.sections"
                    :key="`section-${sIdx}`"
                    :style="{
                      ...section.style,
                      fontSize: `${fontsizeData.section * scale}px`
                    }"
                    style="position: absolute;"
                  >
                    <div
                      v-if="section.icon"
                      class="d-flex justify-center align-center h-full w-full"
                      style="margin-top: -40%; margin-bottom: 5%;"
                    >
                      <img :src="getIconPath(section.icon)"
                      :style="{ width: section.iconsize || '40%',
                      height: 'auto' }"
                    />
                    </div>
                    <div
                      class="text-center d-flex align-center justify-center h-full"
                      :class="$vuetify.theme.dark ? 'white--text' : 'black--text'"
                    >
                      {{ section.name }}
                    </div>
                  </div>

                </div>
              </v-window-item>
            </v-window>
            </div>
        </div>
      </div>
    </v-container>
    <v-footer
      padless
      fixed
      :color="$vuetify.theme.dark ? '#121212' : '#FFFFFF'"
      style="z-index: 10;"
      v-if="!isTV && !isFullscreen && Object.keys(layoutData).length > 1"
    >
      <v-col cols="4" class="text-left py-1"></v-col>
      <v-col class="text-center py-1" cols="4">
        <div>
          <v-btn
            icon
            small
            class="d-inline-block mr-4"
            @click="window = window === 0 ? Object.keys(layoutData).length - 1 : window - 1"
          >
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>

          <v-item-group
            v-model="window"
            class="text-center d-inline-block"
            mandatory
            v-if="$vuetify.breakpoint.smAndUp"
          >
            <v-item
              v-for="(details, layoutKey, i) in layoutData"
              :key="`dot-${i}`"
              v-slot="{ active, toggle }"
            >
              <v-btn icon small @click="toggle">
                <v-icon small :color="active ? ($vuetify.theme.dark ? 'white' : 'black') : 'grey'">
                  mdi-record
                </v-icon>
              </v-btn>
            </v-item>
          </v-item-group>

          <v-btn
            icon
            small
            class="d-inline-block ml-4"
            @click="window = window === Object.keys(layoutData).length - 1 ? 0 : window + 1"
          >
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
        </div>
      </v-col>
      <v-col cols="4" class="text-right py-1"></v-col>
    </v-footer>
    <v-app-bar
      flat
      v-if="isTV || isFullscreen"
      :color="$vuetify.theme.dark ? '#121212' : 'grey lighten-5'"
    >
      <img :src="`/assets/logo/${logoName}.png`" contain :alt="altText" :height="logoSize" />
      <v-toolbar-title class="headline font-weight-medium ml-4">
        <portal-target name="dashboard-title"></portal-target>
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <portal-target name="dashboard-footer"></portal-target>
    </v-app-bar>
  </div>
</template>

<script>
import {
  mapState, mapMutations, mapActions, mapGetters,
} from 'vuex';
import configData from '../ems-layout.json';
import meterImg from '../assets/icons/meter.png';
import mainMeterImg from '../assets/icons/main-meter.png';
import dgImg from '../assets/icons/dg.png';
import DashboardLoading from '../components/widgets/DashboardLoading.vue';

export default {
  name: 'EMSDashboard',
  components: {
    DashboardLoading,
  },
  data() {
    return {
      window: 0,
      scale: 1,
      width: 0,
      height: 0,
      now: '',
      ratio: 1920 / 1080,
      currentimeinterval: null,
      slideinterval: null,
      isInitializing: false,
      isSyncingBusinessTime: false,
      iconMap: {
        'meter.png': meterImg,
        'main-meter.png': mainMeterImg,
        'dg.png': dgImg,
      },
    };
  },
  computed: {
    ...mapState('emsDashboard', [
      'loading',
      'serverTime',
      'meters',
      'config',
      'selectedView',
      'currentDate',
      'currentShift',
      'themes',
      'views',
      'theme', // The object used by Theme.vue
    ]),
    ...mapGetters('helper', ['primaryLogo', 'isTV']),
    ...mapState('helper', ['isFullscreen']),

    activeSite() {
      const { user } = this.$store.state;
      if (user) return user.activeSite || user.activeSiteId || null;
      return null;
    },

    meterDataMap() {
      if (!this.meters || !this.meters.length) return {};
      return this.meters.reduce((acc, curr) => {
        if (curr.metercode) acc[curr.metercode] = curr;
        if (curr.metername) acc[curr.metername] = curr;
        return acc;
      }, {});
    },

    layoutData() {
      return this.config && this.config.layout ? this.config.layout : {};
    },
    hasLayout() {
      return Object.keys(this.layoutData).length > 0;
    },
    unitData() {
      return this.config && this.config.units ? this.config.units : [];
    },
    fontsizeData() {
      const def = { unit: 16, meter: 24, section: 20 };
      return this.config && this.config.fontsize ? this.config.fontsize : def;
    },
    slideInterval() {
      return this.config && this.config.slideIntervalInSec ? this.config.slideIntervalInSec : 5;
    },
    logoName() {
      return this.$vuetify.theme.dark
        ? `${this.primaryLogo.name}-dark`
        : `${this.primaryLogo.name}-light`;
    },
    logoSize() {
      return this.primaryLogo.size;
    },
    altText() {
      return `${this.primaryLogo.name} logo`;
    },
  },
  watch: {
    $route: {
      immediate: true,
      handler(val) {
        this.setQueryParams(val.query || {});
      },
    },

    'theme.isDark': {
      handler(newIsDark) {
        this.applyThemeColors(newIsDark);

        const themeStr = newIsDark ? 'dark' : 'light';
        localStorage.setItem('dark', newIsDark);

        if (this.$route.query.theme !== themeStr) {
          const newQuery = { ...this.$route.query, theme: themeStr };
          this.$router.replace({ query: newQuery }).catch(() => {});
        }
      },
    },
    async selectedView(val, oldVal) {
      if (this.isInitializing) return;
      if (val && oldVal && val !== oldVal) {
        this.setLoading(true);
        try {
          await this.getMeters();
        } finally {
          this.setLoading(false);
        }
      }
    },
    async activeSite(newVal, oldVal) {
      if (this.isInitializing) return;
      if (newVal && newVal !== oldVal) {
        this.setSiteId(newVal);
        this.setLoading(true);
        try {
          await this.getMeters();
        } finally {
          this.setLoading(false);
        }
      }
    },
    serverTime() {
      this.setTime();
    },
  },
  async created() {
    this.isInitializing = true;
    this.setLoading(true);
    try {
      await this.getMe();

      this.setConfig(configData);

      await this.getShifts();
      await this.syncBusinessTime();

      if (this.activeSite && this.meters.length === 0) {
        await this.getMeters();
      }
    } catch (error) {
      console.error('[Dashboard] Error during initialization:', error);
    } finally {
      this.isInitializing = false;
      this.setLoading(false);
    }
  },
  mounted() {
    if (this.theme) {
      this.applyThemeColors(this.theme.isDark);
    }
    this.onResize();
    this.setTime();

    this.currentimeinterval = setInterval(async () => {
      const businessDateChanged = await this.syncBusinessTime();
      if (businessDateChanged && this.activeSite) {
        await this.getMeters();
      }
    }, 60000);

    this.slideinterval = setInterval(() => {
      const slideCount = Object.keys(this.layoutData).length;
      if (slideCount > 1) {
        if (this.window + 1 === slideCount) {
          this.window = 0;
        } else {
          this.window += 1;
        }
      }
    }, this.slideInterval * 1000);
  },
  destroyed() {
    clearInterval(this.currentimeinterval);
    clearInterval(this.slideinterval);
  },
  methods: {
    ...mapMutations('emsDashboard', [
      'setLoading',
      'setSelectedView',
      'setSelectedTheme',
      'setConfig',
      'setSiteId',
      'setTheme',
    ]),
    ...mapActions('user', ['getMe']),
    ...mapActions('emsDashboard', [
      'getMeters',
      'getBusinessTime',
      'getShifts',
    ]),

    async syncBusinessTime() {
      if (this.isSyncingBusinessTime) return false;

      this.isSyncingBusinessTime = true;
      const previousBusinessDate = this.currentDate;

      try {
        await this.getBusinessTime();
        return Boolean(this.currentDate && this.currentDate !== previousBusinessDate);
      } finally {
        this.setTime();
        this.isSyncingBusinessTime = false;
      }
    },

    applyThemeColors(isDark) {
      if (!this.theme || !this.theme.themes) return;

      const activeTheme = isDark ? 'dark' : 'light';
      this.$vuetify.theme.dark = isDark;

      const customColors = this.theme.themes[activeTheme];
      Object.entries(customColors).forEach(([key, value]) => {
        this.$vuetify.theme.themes[activeTheme][key] = value;
      });
    },

    setQueryParams(query) {
      let { theme, view } = query;

      if (!this.themes.includes(theme)) {
        const isDark = localStorage.getItem('dark') === 'true';
        theme = isDark ? 'dark' : 'light';
      }
      if (!this.views.includes(view)) {
        view = 'shift';
      }
      if (this.theme) {
        const urlIsDark = theme === 'dark';
        if (this.theme.isDark !== urlIsDark) {
          this.setTheme({ ...this.theme, isDark: urlIsDark });
        }
      }

      if (query.theme !== theme || query.view !== view) {
        this.$router.replace({ query: { ...query, theme, view } }).catch(() => {});
      }

      this.setSelectedTheme(theme);
      this.setSelectedView(view);
    },

    getUnitValue(meterName, unitValue) {
      const meter = this.meterDataMap[meterName];
      if (meter) {
        const key = String(unitValue).toLowerCase();

        if (meter[key] !== undefined && meter[key] !== null) {
          return meter[key];
        }
      }
      return 0;
    },
    onResize() {
      const maxWidth = window.innerWidth;

      let adjust = this.isTV || this.isFullscreen ? 64 : 120;

      if (!this.isTV && !this.isFullscreen && Object.keys(this.layoutData).length > 1) {
        adjust += 48;
      }

      const maxHeight = window.innerHeight - adjust;

      let newWidth = maxWidth;
      let newHeight = newWidth / this.ratio;

      if (newHeight > maxHeight) {
        newHeight = maxHeight;
        newWidth = newHeight * this.ratio;
      }

      this.height = Math.max(newHeight, 100);
      this.width = Math.max(newWidth, 100);
      this.scale = this.width / 1920;
    },

    setTime() {
      this.now = this.dateFormat();
    },
    dateFormat() {
      if (!this.serverTime) return '';
      const date = new Date(this.serverTime);
      const day = new Intl.DateTimeFormat('en', { day: '2-digit' }).format(date);
      const year = new Intl.DateTimeFormat('en', { year: 'numeric' }).format(date);
      const month = new Intl.DateTimeFormat('en', { month: 'short' }).format(date);
      const hour = (`0${date.getHours()}`).slice(-2);
      const min = (`0${date.getMinutes()}`).slice(-2);
      return `${day} ${month} ${year} | ${hour}:${min}`;
    },
    getIconPath(iconName) {
      if (!iconName) return '';
      return this.iconMap[iconName] || '';
    },
  },
};
</script>

<style scoped>
.w-full {
  width: 100%;
}
.h-full {
  height: 100%;
}
</style>
